import styles from "../../pages/replacements-page-styles.module.css";
import { GridLegacy as Grid } from "@mui/material";
import {
	ReplacementReviewNoShipAddress,
	ReplacementReviewShippingMethodHeader,
	ReplacementReviewShippingToHeader,
	ReplacementShipToAddressSubheaderText,
	ReplacementShipToDeliveryContactSubheaderText,
	ReplacementShipToFormDeliveryText,
	ReplacementShipToFormPickupText,
	ReplacementsShipToFormShippingLabelNotes,
	UnavailableDataPlaceholderText
} from "constants/text";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { formatPhoneNumber } from "components/Replacements/ReplacementsUtil";
import { models } from "types/api/viewModels.ts";
import { formatAddress } from "utils/address.ts";

export interface ReplacementShipToProps {
	orderDetail: models["OrderDetailViewModel"] | null | undefined;
	accounts: models["CustomerAccountViewModel"] | undefined;
}

const ReplacementShipTo = ({ orderDetail, accounts }: ReplacementShipToProps) => {
	const details = useSelector((state: RootState) => state.submittingReplacementOrder.order);
	const shippingInfo: (models["ShipToViewModel"] | null | undefined)[] = useMemo(
		() => accounts?.billTos?.map((address) => address.shipTos)?.flat() ?? [],
		[accounts?.billTos]
	);

	const [apiAddress] = useMemo(() => shippingInfo?.filter((address) => address?.isSelected) ?? [], [shippingInfo]);

	const selectedShippingAddress = details?.shippingDetails ?? apiAddress;

	const phoneNumber = formatPhoneNumber(selectedShippingAddress?.address?.phoneNumber ?? undefined);
	const hasAddress = Boolean(selectedShippingAddress?.address?.city);
	const isCustomerPickup = details?.isCustomerPickup;

	const isReview = window.location.toString().includes("review");

	return (
		<Grid
			container
			className={`${styles.orderDetailContainer} ${isReview ? "" : styles.shippingAndBillingInfo} `}
		>
			<Grid
				container
				xs={12}
				justifyContent="space-between"
			>
				<Grid item>
					<span className="subtitle1">{ReplacementReviewShippingToHeader}</span>
				</Grid>
			</Grid>
			<Grid
				container
				direction="column"
				xs={12}
				md={6}
			>
				<Grid item>
					<span className="subtitle2">{ReplacementShipToAddressSubheaderText}</span>
				</Grid>
				{(!hasAddress || isCustomerPickup) && (
					<Grid
						container
						sx={{ width: "100%" }}
					>
						<Grid item>
							<span
								className="body2"
								data-testid="shipTo-unavailable-for-pickup"
							>
								{UnavailableDataPlaceholderText}
							</span>
						</Grid>
						{!isCustomerPickup && (
							<Grid
								item
								xs={12}
							>
								<div
									className={styles.orderListErrorsWarning}
									data-testid="replacement-review-no-ship-to-address"
								>
									<WarningAmber />
									<span>{ReplacementReviewNoShipAddress}</span>
								</div>
							</Grid>
						)}
					</Grid>
				)}
				{selectedShippingAddress && hasAddress && !isCustomerPickup && (
					<Grid
						container
						direction="column"
						className={styles.shipToComponentSection}
					>
						<span
							className="body2"
							data-testid="ship-to-name"
							style={{ whiteSpace: "pre-line" }}
						>
							{formatAddress(selectedShippingAddress.address)}
						</span>
					</Grid>
				)}
			</Grid>

			{selectedShippingAddress && (
				<Grid
					container
					direction="column"
					xs={12}
					md={6}
				>
					<Grid item>
						<span className="subtitle2">{ReplacementShipToDeliveryContactSubheaderText}</span>
					</Grid>
					<Grid item>
						<span
							className="body2"
							data-testid="ship-to-phone"
						>
							{phoneNumber && phoneNumber.length !== 0 ? phoneNumber : UnavailableDataPlaceholderText}
						</span>
					</Grid>
					<Grid
						item
						className={styles.shipToComponentSection}
					>
						<span
							className="body2"
							data-testid="ship-to-email"
						>
							{details?.shippingDetails?.address?.email ?? ""}
						</span>
					</Grid>
					<Grid item>
						<span className="subtitle2">{ReplacementReviewShippingMethodHeader}</span>
					</Grid>
					<Grid item>
						<span
							className="body2"
							data-testid="replacement-ship-to-shipping-method"
						>
							{details?.isCustomerPickup
								? ReplacementShipToFormPickupText
								: ReplacementShipToFormDeliveryText}
						</span>
					</Grid>
				</Grid>
			)}
			{accounts?.allowLabelComments && !details?.isCustomerPickup && (
				<Grid
					container
					xs={12}
					sm={6}
					direction="column"
				>
					<Grid
						item
						sx={{
							"@media (max-width: 900px)": {
								paddingTop: "1rem"
							}
						}}
					>
						<span className="subtitle2">{ReplacementsShipToFormShippingLabelNotes}</span>
						<br />
						<span
							className="body2"
							data-testid="ship-to-comments"
						>
							{(details?.labelComments ? details?.labelComments : UnavailableDataPlaceholderText) ??
								(orderDetail?.labelComments
									? orderDetail?.labelComments
									: UnavailableDataPlaceholderText)}
						</span>
					</Grid>
				</Grid>
			)}
		</Grid>
	);
};

export default ReplacementShipTo;
